import { Box, Card, Flex, Heading, Text, TextField, Button, Separator } from "@radix-ui/themes";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { registerSchema, type RegisterInput } from "@/lib/schemas";
import { register as apiRegister } from "@/lib/api";
import { useAuthStore } from "@/stores/auth.store";
import { FieldError } from "@/components/ui/field-error";
import { LanguageSwitcher } from "@/components/ui/language-switcher";

export function RegisterPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
  });

  async function onSubmit(data: RegisterInput) {
    const result = await apiRegister(data.email, data.password, data.name);
    if (!result.ok) {
      toast.error(result.error ?? t("auth.register_error"));
      return;
    }
    setAuth(result.data.user, result.data.token);
    toast.success(t("auth.register_success"));
    navigate({ to: "/dashboard" });
  }

  return (
    <Flex
      align="center"
      justify="center"
      style={{ minHeight: "100vh", backgroundColor: "var(--gray-2)" }}
    >
      <Box style={{ width: "100%", maxWidth: "420px" }} p="4">
        {/* Header */}
        <Flex direction="column" align="center" gap="1" mb="6">
          <Heading size="6" weight="bold">
            {t("common.app_name")}
          </Heading>
          <Text size="2" color="gray">
            {t("auth.register_family_subtitle")}
          </Text>
        </Flex>

        {/* Card */}
        <Card size="3">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Flex direction="column" gap="4">
              {/* Prénom */}
              <Flex direction="column" gap="1">
                <Text as="label" size="2" weight="medium" htmlFor="name">
                  {t("auth.first_name_label")}
                </Text>
                <TextField.Root
                  id="name"
                  type="text"
                  placeholder="Marie"
                  autoComplete="given-name"
                  size="3"
                  {...register("name")}
                  color={errors.name ? "red" : undefined}
                />
                <FieldError message={errors.name?.message} />
              </Flex>

              {/* Email */}
              <Flex direction="column" gap="1">
                <Text as="label" size="2" weight="medium" htmlFor="email">
                  {t("auth.email_label")}
                </Text>
                <TextField.Root
                  id="email"
                  type="email"
                  placeholder="vous@exemple.com"
                  autoComplete="email"
                  size="3"
                  {...register("email")}
                  color={errors.email ? "red" : undefined}
                />
                <FieldError message={errors.email?.message} />
              </Flex>

              {/* Mot de passe */}
              <Flex direction="column" gap="1">
                <Text as="label" size="2" weight="medium" htmlFor="password">
                  {t("auth.password_label")}
                </Text>
                <TextField.Root
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  size="3"
                  {...register("password")}
                  color={errors.password ? "red" : undefined}
                />
                <FieldError message={errors.password?.message} />
              </Flex>

              {/* Confirmation */}
              <Flex direction="column" gap="1">
                <Text as="label" size="2" weight="medium" htmlFor="confirm">
                  {t("auth.password_confirm_label")}
                </Text>
                <TextField.Root
                  id="confirm"
                  type="password"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  size="3"
                  {...register("confirm")}
                  color={errors.confirm ? "red" : undefined}
                />
                <FieldError message={errors.confirm?.message} />
              </Flex>

              {/* Submit */}
              <Button
                type="submit"
                size="3"
                style={{ width: "100%", marginTop: "var(--space-1)" }}
                disabled={isSubmitting}
              >
                {isSubmitting ? t("auth.register_pending") : t("auth.register_button")}
              </Button>
            </Flex>
          </form>

          <Separator size="4" my="4" />

          <Text size="2" color="gray" align="center" as="p">
            {t("auth.already_account")}{" "}
            <Link to="/login" style={{ textDecoration: "none" }}>
              <Text size="2" color="violet">
                {t("auth.login_link")}
              </Text>
            </Link>
          </Text>
        </Card>

        {/* Sélecteur de langue discret */}
        <Flex justify="center" mt="4">
          <LanguageSwitcher />
        </Flex>
      </Box>
    </Flex>
  );
}
